/* eslint-disable */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  LogOut,
  UserCog,
  ChevronDown,
  Check,
} from "lucide-react";
import NotificationBell from "../notifications/NotificationBell";
import ImpersonationBanner from "../impersonate/ImpersonationBanner";
import ImpersonatePicker from "../impersonate/ImpersonatePicker";
import { useAuth } from "../auth/AuthContext";
import "./AppShell.css";

// Human label for each app role the web switcher may surface. Kept local to
// the web (CRA can't import the shared/ module outside src/); mirrors
// shared/constants/auth.js APP_ROLE_LABEL.
const ROLE_LABEL = {
  ADMIN: "Admin",
  SECURITY_INCHARGE: "Security Incharge",
  FACILITY_MANAGER: "Facility Manager",
  CLUBHOUSE_SECURITY: "Clubhouse",
  SECURITY_GUARD: "Guard",
  SERVICE_STAFF: "Staff",
  RESIDENT: "Resident",
};

export default function AppShell({ user, navItems, onLogout, children }) {
  const location = useLocation();
  const {
    isItAdmin,
    isImpersonating,
    activeRole,
    availableRoles,
    switchRole,
  } = useAuth();
  const [collapsed, setCollapsed] = useState(
    localStorage.getItem("vs_sidebar_collapsed") === "true"
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  // A multi-role user (e.g. an admin who is also a primary owner) can flip
  // which view they're in. Single-role accounts see no switcher.
  const canSwitchRole = Array.isArray(availableRoles) && availableRoles.length > 1;

  const onPickRole = async (role) => {
    setRoleMenuOpen(false);
    if (role === activeRole || switching) return;
    setSwitching(true);
    try {
      await switchRole(role);
      // Land on home — the previous route may be hidden in the new view.
      if (window.location.hash && window.location.hash !== "#/") {
        window.location.hash = "#/";
      }
    } catch (_) {
      // Switch failed (e.g. role no longer held) — stay where we are.
    } finally {
      setSwitching(false);
    }
  };

  useEffect(() => {
    localStorage.setItem("vs_sidebar_collapsed", collapsed ? "true" : "false");
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const currentPath = location.pathname || "/";
  const isActive = (item) => {
    if (item.match) return item.match(currentPath);
    if (item.to === "/") return currentPath === "/";
    return currentPath === item.to || currentPath.startsWith(item.to + "/");
  };

  const renderItem = (item) => {
    const active = isActive(item);
    const Icon = item.Icon;
    const inner = (
      <>
        <span
          className="vsa-nav-icon"
          style={{ background: item.iconBg || "#F1F5F9" }}
        >
          {Icon && (
            <Icon
              size={16}
              strokeWidth={1.8}
              color={item.iconColor || "#475569"}
            />
          )}
        </span>
        <span className="vsa-nav-label">{item.label}</span>
        {active && (
          <span
            className="vsa-nav-active-dot"
            style={{ background: item.iconColor || "var(--accent)" }}
          />
        )}
      </>
    );

    if (item.disabled) {
      return (
        <button
          key={item.key}
          className={`vsa-nav-item ${active ? "active" : ""}`}
          disabled
          title={item.label}
        >
          {inner}
        </button>
      );
    }
    return (
      <Link
        key={item.key}
        to={item.to}
        className={`vsa-nav-item ${active ? "active" : ""}`}
        title={item.label}
      >
        {inner}
      </Link>
    );
  };

  const sidebar = (
    <aside
      className={`vsa-sidebar ${mobileOpen ? "mobile-open" : ""} ${
        collapsed ? "collapsed" : ""
      }`}
    >
      <button
        className="vsa-collapse-floater"
        onClick={() => setCollapsed((c) => !c)}
        aria-label="Toggle sidebar"
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
      </button>

      <nav className="vsa-nav">
        <div className="vsa-nav-section-label">Main</div>
        {navItems.map(renderItem)}
      </nav>

      <button className="vsa-sidebar-logout" onClick={onLogout} title="Logout">
        <LogOut size={17} strokeWidth={1.9} />
        <span className="vsa-nav-label">Logout</span>
      </button>
    </aside>
  );

  return (
    <div className={`vsa-shell ${collapsed ? "collapsed" : ""}`}>
      <header className="vsa-header">
        <div className="vsa-header-left">
          <button
            className="vsa-hamburger"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon size={18} />
          </button>
          <Link to="/profile" className="vsa-header-id" title="Your profile">
            <span className="vsa-header-avatar">
              {(user?.name || user?.phone_number || "?")
                .trim()
                .charAt(0)
                .toUpperCase()}
            </span>
            <span className="vsa-header-id-meta">
              <span className="vsa-header-id-name">
                {user?.name || user?.phone_number}
              </span>
              <span className="vsa-header-id-role">
                {user?.is_admin
                  ? user.admin_role || "Admin"
                  : user?.user_type === "PRIMARY_USER"
                  ? "Primary"
                  : "Member"}
              </span>
            </span>
          </Link>
        </div>
        <div className="vsa-header-right">
          {canSwitchRole && (
            <div className="vsa-role-switch">
              <button
                className="vsa-role-trigger"
                onClick={() => setRoleMenuOpen((o) => !o)}
                disabled={switching}
                title="Switch view"
              >
                <span>{ROLE_LABEL[activeRole] || activeRole}</span>
                <ChevronDown size={14} />
              </button>
              {roleMenuOpen && (
                <>
                  <div
                    className="vsa-role-backdrop"
                    onClick={() => setRoleMenuOpen(false)}
                  />
                  <div className="vsa-role-menu" role="menu">
                    {availableRoles.map((role) => (
                      <button
                        key={role}
                        className="vsa-role-option"
                        role="menuitemradio"
                        aria-checked={role === activeRole}
                        onClick={() => onPickRole(role)}
                      >
                        <span>{ROLE_LABEL[role] || role}</span>
                        {role === activeRole && <Check size={14} />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {isItAdmin && !isImpersonating && (
            <button
              className="vsi-trigger"
              onClick={() => setPickerOpen(true)}
              title="Sign in as another user"
            >
              <UserCog size={14} />
              <span>Impersonate</span>
            </button>
          )}
          <NotificationBell />
        </div>
      </header>

      <ImpersonationBanner />

      <div className="vsa-body">
        {mobileOpen && (
          <div
            className="vsa-mobile-backdrop"
            onClick={() => setMobileOpen(false)}
          />
        )}
        {sidebar}
        <main className="vsa-main">{children}</main>
      </div>

      <ImpersonatePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
}
